import React, { useState, useMemo, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Loader2, Search, RefreshCw, Settings, Info, Eye, EyeOff, CheckCircle2, XCircle, AlertCircle, Key, X } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/useToastHistory'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'

interface Client {
  id: string
  client_id: number
  name: string
  masterinbox_api_key: string | null
  perplexity_api_key: string | null
  ai_categorization_enabled: boolean | null
}

export function MasterInboxApiKeysTab() {
  const [clients, setClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [editingClient, setEditingClient] = useState<string | null>(null)
  const [editingData, setEditingData] = useState<{
    masterinbox_api_key: string
    perplexity_api_key: string
    ai_categorization_enabled: boolean
  } | null>(null)
  const [showApiKeys, setShowApiKeys] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [validating, setValidating] = useState<string | null>(null)
  const [validationStatus, setValidationStatus] = useState<Record<string, { valid: boolean; message: string }>>({})
  // Track which API keys have been validated (clientId -> validated API key)
  const [validatedApiKeys, setValidatedApiKeys] = useState<Record<string, string>>({})


  useEffect(() => {
    fetchClients()
  }, [])

  const fetchClients = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const { data, error: fetchError } = await supabase
        .from('clients')
        .select('id, client_id, name, status, masterinbox_api_key, perplexity_api_key, ai_categorization_enabled')
        .eq('status', 'Active') // Only show Active clients
        .neq('id', 'default') // Exclude system default client
        .not('name', 'ilike', '%global%default%keys%') // Exclude Global Default Keys
        .not('name', 'ilike', '%default%keys%') // Extra filter for safety
        .not('name', 'ilike', '%outboundbuddy%') // Exclude Outboundbuddy
        .order('name')

      if (fetchError) throw fetchError

      setClients(data || [])
    } catch (err: any) {
      console.error('Error fetching clients:', err)
      setError(err.message || 'Failed to load clients')
      toast({
        title: 'Error',
        description: 'Failed to load clients',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const filteredClients = useMemo(() => {
    let filtered = clients

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(client =>
        client.name.toLowerCase().includes(searchLower) ||
        client.client_id.toString().includes(searchLower)
      )
      
      // Always include the client being edited, even if it doesn't match the search
      if (editingClient) {
        const editingClientData = clients.find(c => c.id === editingClient)
        if (editingClientData && !filtered.find(c => c.id === editingClient)) {
          filtered = [editingClientData, ...filtered]
        }
      }
    }

    return filtered
  }, [clients, searchTerm, editingClient])

  const configuredCount = useMemo(() => {
    return clients.filter(c => c.masterinbox_api_key).length
  }, [clients])

  const handleEdit = (client: Client) => {
    setEditingClient(client.id)
    // Clear search when entering edit mode to ensure client is visible
    setSearchTerm('')
    // Explicitly set to empty string if null/undefined to prevent browser autofill
    const apiKey = client.masterinbox_api_key ? String(client.masterinbox_api_key) : ''
    const perplexityKey = client.perplexity_api_key ? String(client.perplexity_api_key) : ''
    setEditingData({
      masterinbox_api_key: apiKey,
      perplexity_api_key: perplexityKey,
      ai_categorization_enabled: client.ai_categorization_enabled ?? false
    })
  }

  const handleCancel = () => {
    setEditingClient(null)
    setEditingData(null)
  }

  const handleSave = async (clientId: string) => {
    if (!editingData) return

    setSaving(clientId)
    try {
      // Get the original client data to compare Master Inbox API key
      const originalClient = clients.find(c => c.id === clientId)
      const originalMasterInboxKey = originalClient?.masterinbox_api_key || ''
      
      // If ai_categorization_enabled is true but no perplexity_api_key, disable it
      const shouldEnableCategorization = editingData.ai_categorization_enabled && editingData.perplexity_api_key.trim()
      
      // Ensure boolean type (not string)
      const aiCategorizationEnabled = Boolean(shouldEnableCategorization)
      
      const { error: updateError } = await supabase
        .from('clients')
        .update({
          masterinbox_api_key: editingData.masterinbox_api_key || null,
          perplexity_api_key: editingData.perplexity_api_key || null,
          ai_categorization_enabled: aiCategorizationEnabled
        })
        .eq('id', clientId)

      if (updateError) throw updateError

      toast({
        title: 'Success',
        description: 'API keys and settings updated'
      })

      // Store the validated key and original key before fetching
      const validatedKey = validatedApiKeys[clientId]
      const savedMasterInboxKey = editingData.masterinbox_api_key
      const masterInboxKeyChanged = originalMasterInboxKey !== savedMasterInboxKey

      // Auto-populate labels + fetch inbox types when MasterInbox API key is set/changed
      if (savedMasterInboxKey && savedMasterInboxKey.trim() && masterInboxKeyChanged) {
        try {
          const { data: fetchData, error: fetchError } = await supabase.functions.invoke('fetch-masterinbox-labels', {
            body: { client_id: clientId }
          })
          if (fetchError) {
            console.error('Error auto-fetching inbox types:', fetchError)
          } else if (fetchData?.success) {
            toast({
              title: 'Lists Fetched',
              description: `Auto-fetched ${fetchData.inbox_types_count || 0} inbox types for ${fetchData.client_name || 'client'}. Labels ${fetchData.labels_populated === 'auto-populated' ? 'auto-populated' : 'already set'}.`
            })
          }
        } catch (autoFetchErr) {
          console.error('Error auto-fetching inbox types:', autoFetchErr)
        }
      }

      await fetchClients()
      setEditingClient(null)
      setEditingData(null)

      // Keep validation status ONLY if:
      // 1. There was a validated key
      // 2. The Master Inbox API key was NOT changed (compare with original)
      // Only clear validation if the Master Inbox API key was actually modified
      if (validatedKey && masterInboxKeyChanged) {
        // Master Inbox API key was changed, clear validation
        setValidatedApiKeys(prev => {
          const updated = { ...prev }
          delete updated[clientId]
          return updated
        })
        setValidationStatus(prev => {
          const updated = { ...prev }
          delete updated[clientId]
          return updated
        })
      }
      // If Master Inbox API key was NOT changed, keep validation status (do nothing)
    } catch (err: any) {
      console.error('Error updating client:', err)
      toast({
        title: 'Error',
        description: err.message || 'Failed to update API key',
        variant: 'destructive'
      })
    } finally {
      setSaving(null)
    }
  }

  const handleValidate = async (clientId: string, apiKey: string) => {
    if (!apiKey || !apiKey.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an API key first',
        variant: 'destructive'
      })
      return
    }

    setValidating(clientId)
    setValidationStatus(prev => ({
      ...prev,
      [clientId]: { valid: false, message: 'Validating...' }
    }))

    try {
      const { data, error: validateError } = await supabase.functions.invoke('masterinbox-validate-key', {
        body: { api_key: apiKey }
      })

      if (validateError) throw validateError

      const isValid = data?.valid === true
      const message = data?.message || (isValid ? 'API key is valid' : 'API key is invalid')

      setValidationStatus(prev => ({
        ...prev,
        [clientId]: { valid: isValid, message }
      }))

      // If validation succeeded, store the validated API key
      if (isValid) {
        setValidatedApiKeys(prev => ({
          ...prev,
          [clientId]: apiKey
        }))
      } else {
        // Clear validation state if validation failed
        setValidatedApiKeys(prev => {
          const updated = { ...prev }
          delete updated[clientId]
          return updated
        })
      }

      toast({
        title: isValid ? 'Success' : 'Validation Failed',
        description: message,
        variant: isValid ? 'default' : 'destructive'
      })
    } catch (err: any) {
      console.error('Error validating API key:', err)
      setValidationStatus(prev => ({
        ...prev,
        [clientId]: { valid: false, message: err.message || 'Validation failed' }
      }))
      toast({
        title: 'Error',
        description: err.message || 'Failed to validate API key',
        variant: 'destructive'
      })
    } finally {
      setValidating(null)
    }
  }

  const toggleApiKeyVisibility = (clientId: string) => {
    setShowApiKeys(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading Master Inbox API key settings...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error Loading Settings</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Master Inbox API Keys
              </CardTitle>
              <CardDescription className="mt-2">
                Manage Master Inbox and Perplexity API keys for each client workspace. Each client requires their own API keys to access their Master Inbox workspace and enable AI categorization.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchClients}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">{clients.length}</div>
              <div className="text-sm text-muted-foreground">Total Clients</div>
            </div>
            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-500">{configuredCount}</div>
              <div className="text-sm text-muted-foreground">API Keys Configured</div>
            </div>
            <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-500">{clients.length - configuredCount}</div>
              <div className="text-sm text-muted-foreground">Not Configured</div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Info Alert */}
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertTitle>How It Works</AlertTitle>
            <AlertDescription className="text-sm mt-2">
              Each client has their own Master Inbox workspace and Perplexity API key. Configure the Master Inbox API key 
              to enable API calls to their workspace. Configure the Perplexity API key and enable AI Categorization to use 
              AI-powered categorization features. Use the "Verify" button to test if a Master Inbox API key is valid before saving.
            </AlertDescription>
          </Alert>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                name="client-search"
              />
              {searchTerm && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-transparent"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client Cards Grid */}
      {filteredClients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-[200px] text-center">
            <Settings className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-lg font-medium">No clients found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchTerm ? 'Try a different search term' : 'No active clients available'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredClients.map(client => {
            const isEditing = editingClient === client.id
            const clientData = isEditing && editingData ? editingData : {
              masterinbox_api_key: client.masterinbox_api_key || '',
              perplexity_api_key: client.perplexity_api_key || '',
              ai_categorization_enabled: client.ai_categorization_enabled ?? false
            }
            const showKey = showApiKeys[client.id] || false
            const validation = validationStatus[client.id]
            const isValidating = validating === client.id
            // Check if current API key has been validated
            const isValidated = validatedApiKeys[client.id] === clientData.masterinbox_api_key && validation?.valid === true

            return (
              <Card key={client.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{client.name}</CardTitle>
                      <CardDescription className="mt-1">Client ID: {client.client_id}</CardDescription>
                    </div>
                    <Badge 
                      variant={client.masterinbox_api_key ? 'default' : 'secondary'}
                      className={client.masterinbox_api_key ? 'bg-green-600 text-white border-green-600 hover:bg-green-700' : ''}
                    >
                      {client.masterinbox_api_key ? (
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                      ) : (
                        <XCircle className="h-3 w-3 mr-1" />
                      )}
                      {client.masterinbox_api_key ? 'Configured' : 'Not Configured'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Master Inbox API Key */}
                  <div className="space-y-2">
                    <Label htmlFor={`masterinbox-${client.id}`}>Master Inbox API Key</Label>
                    {isEditing ? (
                      <div className="space-y-2">
                        <div className="relative">
                          <Input
                            id={`masterinbox-${client.id}`}
                            name={`masterinbox-api-key-${client.id}`}
                            type={showKey ? 'text' : 'password'}
                            value={clientData.masterinbox_api_key}
                            onChange={(e) => {
                              const newKey = e.target.value
                              setEditingData(prev => prev ? { ...prev, masterinbox_api_key: newKey } : null)
                              // Reset validation state if API key changes
                              if (validatedApiKeys[client.id] && validatedApiKeys[client.id] !== newKey) {
                                setValidatedApiKeys(prev => {
                                  const updated = { ...prev }
                                  delete updated[client.id]
                                  return updated
                                })
                                setValidationStatus(prev => {
                                  const updated = { ...prev }
                                  delete updated[client.id]
                                  return updated
                                })
                              }
                            }}
                            placeholder="Enter Master Inbox API key"
                            autoComplete="off"
                            data-form-type="other"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            data-bwignore="true"
                            readOnly={false}
                            onFocus={(e) => {
                              // Clear any autofilled value on focus
                              if (e.target.value && !client.masterinbox_api_key) {
                                e.target.value = ''
                                setEditingData(prev => prev ? { ...prev, masterinbox_api_key: '' } : null)
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                            onClick={() => toggleApiKeyVisibility(client.id)}
                          >
                            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant={isValidated ? "default" : "outline"}
                          size="sm"
                          className={`w-full ${isValidated ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                          onClick={() => handleValidate(client.id, clientData.masterinbox_api_key)}
                          disabled={isValidating || !clientData.masterinbox_api_key.trim()}
                        >
                          {isValidating ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Validating...
                            </>
                          ) : isValidated ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Validated Key
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Verify API Key
                            </>
                          )}
                        </Button>
                        {validation && !validation.valid && (
                          <Alert variant="destructive" className="py-2">
                            <div className="flex items-center gap-2">
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription className="text-xs m-0">
                                {validation.message}
                              </AlertDescription>
                            </div>
                          </Alert>
                        )}
                        {validation && validation.valid && isValidating && (
                          <Alert variant="default" className="py-2">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4" />
                              <AlertDescription className="text-xs m-0">
                                {validation.message}
                              </AlertDescription>
                            </div>
                          </Alert>
                        )}
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          id={`masterinbox-${client.id}`}
                          type={showKey ? 'text' : 'password'}
                          value={client.masterinbox_api_key || ''}
                          disabled
                          placeholder="Not configured"
                          autoComplete="off"
                          data-form-type="other"
                        />
                        {client.masterinbox_api_key && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                            onClick={() => toggleApiKeyVisibility(client.id)}
                          >
                            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Perplexity API Key */}
                  <div className="space-y-2">
                    <Label htmlFor={`perplexity-${client.id}`}>Perplexity API Key</Label>
                    {isEditing ? (
                      <div className="space-y-2">
                        <div className="relative">
                          <Input
                            id={`perplexity-${client.id}`}
                            name={`perplexity-api-key-${client.id}`}
                            type={showKey ? 'text' : 'password'}
                            value={clientData.perplexity_api_key}
                            onChange={(e) => {
                              const newKey = e.target.value
                              setEditingData(prev => {
                                if (!prev) return null
                                // If API key is cleared, disable categorization
                                const shouldDisableCategorization = !newKey.trim() && prev.ai_categorization_enabled
                                return {
                                  ...prev,
                                  perplexity_api_key: newKey,
                                  ai_categorization_enabled: shouldDisableCategorization ? false : prev.ai_categorization_enabled
                                }
                              })
                            }}
                            placeholder="Enter Perplexity API key"
                            autoComplete="off"
                            data-form-type="other"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            data-bwignore="true"
                            readOnly={false}
                            onFocus={(e) => {
                              // Clear any autofilled value on focus
                              if (e.target.value && !client.perplexity_api_key) {
                                e.target.value = ''
                                setEditingData(prev => prev ? { ...prev, perplexity_api_key: '' } : null)
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                            onClick={() => toggleApiKeyVisibility(client.id)}
                          >
                            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          id={`perplexity-${client.id}`}
                          type={showKey ? 'text' : 'password'}
                          value={client.perplexity_api_key || ''}
                          disabled
                          placeholder="Not configured"
                          autoComplete="off"
                          data-form-type="other"
                        />
                        {client.perplexity_api_key && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                            onClick={() => toggleApiKeyVisibility(client.id)}
                          >
                            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Enable AI Categorization Toggle */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor={`ai-categorization-${client.id}`} className="cursor-pointer">
                        Enable AI Categorization
                      </Label>
                      <div className="flex items-center gap-2">
                        {isEditing ? (
                          <>
                            <span className={`text-xs font-medium ${clientData.ai_categorization_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                              {clientData.ai_categorization_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <Switch
                              id={`ai-categorization-${client.id}`}
                              checked={clientData.ai_categorization_enabled}
                              onCheckedChange={(checked) => {
                                setEditingData(prev => prev ? { ...prev, ai_categorization_enabled: checked } : null)
                              }}
                              disabled={!clientData.perplexity_api_key.trim()}
                            />
                          </>
                        ) : (
                          <>
                            <span className={`text-xs font-medium ${client.ai_categorization_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                              {client.ai_categorization_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <Switch
                              id={`ai-categorization-${client.id}`}
                              checked={client.ai_categorization_enabled ?? false}
                              disabled
                            />
                          </>
                        )}
                      </div>
                    </div>
                    {isEditing && !clientData.perplexity_api_key.trim() && (
                      <p className="text-xs text-muted-foreground">
                        Add a Perplexity API key to enable AI categorization
                      </p>
                    )}
                    {!isEditing && !client.perplexity_api_key && (
                      <p className="text-xs text-muted-foreground">
                        Configure Perplexity API key to enable AI categorization
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {isEditing ? (
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={() => handleSave(client.id)}
                        disabled={saving === client.id}
                        className="flex-1"
                      >
                        {saving === client.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={saving === client.id}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(client)}
                      className="w-full"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      {client.masterinbox_api_key ? 'Edit API Key' : 'Add API Key'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
